import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTrack } from "../../hooks/useTrack";
import { supabase } from "../../lib/supabase";
import { NewCreativeModal } from "./NewCreativeModal";
import { ReviewCreativeModal } from "./ReviewCreativeModal";
import { VariationsPlayer } from "./VariationsPlayer";
import { notifyChange } from "../notifications/notify";

// Mirror of `creative_statuses` (id 1=Active, 3=Inactive) used to
// label the markdown diff sent to the CSM. Values match the seed data and
// never change at runtime, so a local constant is cheaper than a join.
const STATUS_NAMES = { 1: "Active", 3: "Inactive" };
const statusName = (id) => STATUS_NAMES[id] ?? `status #${id}`;

function formatCreativeStatusMarkdown({ name, statusId }) {
  return [
    `**Creative:** ${name && String(name).trim() ? name : "(unnamed)"}`,
    `**Status:** ${statusName(statusId)}`,
  ].join("\n");
}

function formatVariationStatusMarkdown({ creativeName, variationLabel, statusId }) {
  return [
    `**Creative:** ${creativeName && String(creativeName).trim() ? creativeName : "(unnamed)"}`,
    `**Variation:** ${variationLabel && String(variationLabel).trim() ? variationLabel : "(unlabeled)"}`,
    `**Status:** ${statusName(statusId)}`,
  ].join("\n");
}

export function CreativeScreen() {
  const { effectiveAdvertiserId: advertiserId, isSupportUser, canManageAdvertiserData } = useAuth();
  const track = useTrack();
  const [creatives, setCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(true);
  const [creativesError, setCreativesError] = useState(null);
  const [creativesRefresh, setCreativesRefresh] = useState(0);
  const [selected, setSelected]     = useState(null);
  const [reviewCreative, setReviewCreative] = useState(null);
  const [search, setSearch]         = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const audioRef = useRef(null);
  const [playingCreativeId, setPlayingCreativeId] = useState(null);
  const [playingVariationId, setPlayingVariationId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [toggling, setToggling] = useState(new Set());
  const [togglingVariation, setTogglingVariation] = useState(new Set());
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setCreativesError(advertiserId == null ? "No advertiser linked" : null);
      setCreativesLoading(false);
      return;
    }
    let cancelled = false;
    setCreativesLoading(true);
    setCreativesError(null);
    (async () => {
      const { data, error } = await supabase
        .from("creatives")
        .select("id, name, labels, status_id, duration, script, start_date, end_date, audio_types, creative_statuses(name, color), creative_variations(id, asset_id, labels, url, status_id, creative_statuses(name, color))")
        .eq("advertiser_id", advertiserId)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        setCreativesError(error.message);
        setCreatives([]);
      } else {
        setCreatives((data ?? []).map((r) => ({
          id: r.id,
          status_id: r.status_id ?? 3,
          name: r.name ?? null,
          labels: r.labels ?? [],
          duration: r.duration,
          script: r.script,
          start_date: r.start_date,
          end_date: r.end_date,
          asset_id: r.asset_id ?? null,
          audio_types: Array.isArray(r.audio_types) ? r.audio_types.filter(a => a !== null && typeof a === "object") : [],
          audio_files: (r.creative_variations ?? []).map(v => ({
            url:          v.url ?? null,
            name:         (v.labels ?? []).join(" — ") || r.name || "Unnamed",
            status_id:    v.status_id ?? 2,
            tag:          v.creative_statuses?.name ?? "Active",
            tagColor:     v.creative_statuses?.color ?? "#F90091",
            variation_id: v.id,
          })),
          tag: r.creative_statuses?.name ?? "Active",
          tagColor: r.creative_statuses?.color ?? "#F90091",
          lift: null,
        })));
      }
      setCreativesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId, creativesRefresh]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.volume = volume;
  }, [volume]);

  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) audioRef.current?.pause();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  const handlePlayVariation = (c, v) => {
    if (!v.url) return;
    const isSameVariation = playingVariationId === v.variation_id;
    if (isSameVariation) {
      if (isPlaying) audioRef.current?.pause();
      else audioRef.current?.play().catch((err) => console.error("Audio resume failed:", err));
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    const audio = new window.Audio(v.url);
    audio.volume = volume;
    audio.addEventListener("timeupdate", () => setCurrentTime(audio.currentTime));
    audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
    audio.addEventListener("play", () => setIsPlaying(true));
    audio.addEventListener("pause", () => setIsPlaying(false));
    audio.addEventListener("ended", () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setPlayingCreativeId(null);
      setPlayingVariationId(null);
    });
    audioRef.current = audio;
    setPlayingCreativeId(c.id);
    setPlayingVariationId(v.variation_id);
    setCurrentTime(0);
    setDuration(0);
    audio.play().catch((err) => console.error("Audio play failed:", err, "URL:", v.url));
    track('creative_audio_played', { creative_id: c.id, creative_name: c.name, variation_id: v.variation_id });
  };

  const handleSeek = (c, value) => {
    if (!audioRef.current || playingCreativeId !== c.id) return;
    const t = Math.max(0, Math.min(value, duration));
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const STATUS_META = {
    1: { tag: "Active",   tagColor: "#151515" },
    3: { tag: "Inactive", tagColor: "#555555" },
  };

  const handleToggleActive = async (e, c) => {
    e.stopPropagation();
    if (toggling.has(c.id)) return;
    setToggling(prev => new Set(prev).add(c.id));
    const oldStatusId = c.status_id;
    const newStatusId = oldStatusId === 3 ? 1 : 3;
    setCreatives(prev => prev.map(x => x.id === c.id ? { ...x, status_id: newStatusId, ...STATUS_META[newStatusId] } : x));
    const { error } = await supabase.from("creatives").update({ status_id: newStatusId }).eq("id", c.id);
    if (error) {
      setCreatives(prev => prev.map(x => x.id === c.id ? { ...x, status_id: oldStatusId, ...STATUS_META[oldStatusId] } : x));
    } else {
      // Fire-and-forget CSM notification. Today the toggle UI is admin-only
      // (rendered when `isSupportUser`) and the DB trigger
      // `restrict_creative_status_id_update` enforces the same gate, so this
      // branch never ships in practice — it's wired for the eventual day RLS
      // is relaxed without needing a follow-up code change. The
      // !isSupportUser guard mirrors the pattern used by every other call
      // site so the contract stays uniform.
      if (!isSupportUser) {
        notifyChange({
          supabase,
          eventType: "Creative Gallery - Creative Status",
          advertiserId,
          previousState: formatCreativeStatusMarkdown({ name: c.name, statusId: oldStatusId }),
          newState: formatCreativeStatusMarkdown({ name: c.name, statusId: newStatusId }),
        });
      }
      setCreativesRefresh(r => r + 1);
    }
    setToggling(prev => { const s = new Set(prev); s.delete(c.id); return s; });
  };

  const handleToggleVariation = async (e, c, v) => {
    e.stopPropagation();
    if (togglingVariation.has(v.variation_id)) return;
    setTogglingVariation(prev => new Set(prev).add(v.variation_id));
    const oldStatusId = v.status_id;
    const newStatusId = oldStatusId === 3 ? 1 : 3;
    const isSingleVariation = c.audio_files.length === 1;
    setCreatives(prev => prev.map(x =>
      x.id !== c.id ? x : {
        ...x,
        ...(isSingleVariation ? { status_id: newStatusId, ...STATUS_META[newStatusId] } : {}),
        audio_files: x.audio_files.map(af =>
          af.variation_id === v.variation_id
            ? { ...af, status_id: newStatusId, tag: STATUS_META[newStatusId].tag, tagColor: STATUS_META[newStatusId].tagColor }
            : af
        ),
      }
    ));
    const updates = [
      supabase.from("creative_variations").update({ status_id: newStatusId }).eq("id", v.variation_id),
      ...(isSingleVariation ? [supabase.from("creatives").update({ status_id: newStatusId }).eq("id", c.id)] : []),
    ];
    const results = await Promise.all(updates);
    const hasError = results.some(r => r.error);
    if (hasError) {
      setCreatives(prev => prev.map(x =>
        x.id !== c.id ? x : {
          ...x,
          ...(isSingleVariation ? { status_id: c.status_id, ...STATUS_META[c.status_id] } : {}),
          audio_files: x.audio_files.map(af =>
            af.variation_id === v.variation_id
              ? { ...af, status_id: oldStatusId, tag: STATUS_META[oldStatusId].tag, tagColor: STATUS_META[oldStatusId].tagColor }
              : af
          ),
        }
      ));
    } else if (!isSupportUser) {
      // Single notification per user click even when isSingleVariation also
      // updates the parent creative — the user-perceived action is the
      // variation toggle, so we report at that granularity. Same admin-gate
      // dead-code caveat as handleToggleActive: today the toggle UI and DB
      // RLS are admin-only, so this branch is wired-but-never-fires until
      // the gate is relaxed.
      notifyChange({
        supabase,
        eventType: "Creative Gallery - Variation Status",
        advertiserId,
        previousState: formatVariationStatusMarkdown({
          creativeName: c.name,
          variationLabel: v.name,
          statusId: oldStatusId,
        }),
        newState: formatVariationStatusMarkdown({
          creativeName: c.name,
          variationLabel: v.name,
          statusId: newStatusId,
        }),
      });
    }
    setTogglingVariation(prev => { const s = new Set(prev); s.delete(v.variation_id); return s; });
  };

  const filtered = creatives.filter(c => {
    const searchLower = search.toLowerCase();
    if (!searchLower) return true;
    if (c.name && c.name.toLowerCase().includes(searchLower)) return true;
    if (Array.isArray(c.labels) && c.labels.some(l => l.toLowerCase().includes(searchLower))) return true;
    if (c.tag && c.tag.toLowerCase().includes(searchLower)) return true;
    return false;
  });

  const ACTIVE_LIMIT = 10;
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => (a.status_id === 1 ? 0 : 1) - (b.status_id === 1 ? 0 : 1)),
    [filtered]
  );
  const displayed = useMemo(
    () => !showAll && !search ? sorted.filter(c => c.status_id === 1).slice(0, ACTIVE_LIMIT) : sorted,
    [sorted, showAll, search]
  );
  const hiddenCount = sorted.length - displayed.length;

  return (
    <div style={{ padding:"28px 28px", maxWidth:900, margin:"0 auto" }}>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <div>
          <h2 style={{ fontSize:28, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Creative Gallery</h2>
        </div>
        <button onClick={() => { if (!canManageAdvertiserData) return; audioRef.current?.pause(); setShowNewModal(true); track('creative_new_opened'); }} disabled={advertiserId == null || !canManageAdvertiserData} style={{ padding:"8px 16px", background: advertiserId == null || !canManageAdvertiserData ? "#E2E2E2" : "#151515", color: advertiserId == null || !canManageAdvertiserData ? "#B8B8B8" : "#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor: advertiserId == null || !canManageAdvertiserData ? "default" : "pointer", whiteSpace:"nowrap", flexShrink:0 }}>+ New Creative</button>
      </div>

      <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:10, padding:"10px 14px", marginBottom:20, display:"flex", gap:10, alignItems:"center", boxShadow:"0 2px 6px rgba(0,0,0,0.06)" }}>
        <span style={{ color:"#7b7b7b", fontSize:14 }}>🔍</span>
        <input
          value={search} onChange={e => { audioRef.current?.pause(); setSearch(e.target.value); }}
          placeholder="Search creatives..."
          style={{ flex:1, background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:13 }}
        />
        {search && <button onClick={() => setSearch("")} style={{ background:"none", border:"none", color:"#7b7b7b", fontSize:16, cursor:"pointer", padding:0, lineHeight:1 }}>✕</button>}
      </div>

      {creativesError && (
        <div style={{ padding:12, marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:10, color:"#e53935", fontSize:13 }}>{creativesError}</div>
      )}
      {creativesLoading ? (
        <div style={{ padding:24, color:"#7b7b7b", fontSize:13 }}>Loading creatives…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding:32, textAlign:"center", color:"#7b7b7b", fontSize:14 }}>
          {creatives.length === 0 ? "No creatives yet. Click + New Creative to add one." : "No creatives match your search."}
        </div>
      ) : (
      <>
      <style>{`
        @keyframes creative-wave {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
      `}</style>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        {displayed.map(c => {
          const isThisPlaying = playingCreativeId === c.id;
          const waveColor = c.status_id === 3 ? "#E2E2E2" : "#F90091";
          return (
          <div key={c.id} onClick={() => {
              const expanding = selected !== c.id;
              setSelected(expanding ? c.id : null);
              audioRef.current?.pause();
              if (expanding) track('creative_expanded', { creative_id: c.id, creative_name: c.name, status: c.tag });
            }}
            style={{ background:"#FFFFFF", border:`1px solid ${selected===c.id ? "#F90091" : "#E2E2E2"}`, borderRadius:14, padding:18, cursor:"pointer", minWidth:0, overflow:"hidden", boxShadow: selected===c.id ? "0 4px 18px rgba(249,0,145,0.12)" : "0 2px 10px rgba(0,0,0,0.06)" }}>
            <div style={{ background:"#FAFAFA", borderRadius:10, height:60, marginBottom:14, display:"flex", alignItems:"center", padding:"0 14px", gap:2, opacity: c.status_id === 3 ? 0.4 : 1 }}>
              {Array.from({ length: 44 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    flex:1,
                    height:`${14 + Math.sin(i * 0.7) * 20 + (i % 3) * 4}px`,
                    background: waveColor,
                    borderRadius:2,
                    opacity:0.7,
                    transformOrigin:"center bottom",
                    animation: isThisPlaying && isPlaying ? `creative-wave ${0.3 + (i % 5) * 0.08}s ease-in-out infinite` : "none",
                  }}
                />
              ))}
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", opacity: c.status_id === 3 ? 0.5 : 1 }}>
              <div>
                <div style={{ fontWeight:600, color:"#151515", fontSize:13, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name || "Unnamed creative"}</div>
                {Array.isArray(c.labels) && c.labels.length > 0 && (
                  <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:4 }}>
                    {c.labels.map((lbl, i) => (
                      <span key={i} style={{ fontSize:10, padding:"2px 6px", background:"#EEF2FF", color:"#323084", borderRadius:4 }}>{lbl}</span>
                    ))}
                  </div>
                )}
                <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:4 }}>
                  {Array.isArray(c.audio_types) && c.audio_types.length > 0
                    ? c.audio_types.map(t => {
                        const excl = t.clusivity === "exclude";
                        return (
                          <span key={t.value} style={{ fontSize:10, padding:"2px 6px", background: excl ? "#FFF5F5" : "#F0FFF4", color: excl ? "#e53935" : "#208a3e", borderRadius:4 }}>
                            {excl ? "¬" : ""}{t.display_name}
                          </span>
                        );
                      })
                    : <span style={{ fontSize:10, padding:"2px 6px", background:"#F5F5F5", color:"#7b7b7b", borderRadius:4 }}>All Types</span>
                  }
                </div>
                <div style={{ fontSize:11, color:"#7b7b7b" }}>~{c.duration} seconds</div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                <span style={{ fontSize:9, padding:"2px 8px", borderRadius:20, background: c.status_id === 1 ? "#FFE9F6" : "#F5F5F5", color: c.status_id === 1 ? "#151515" : c.tagColor, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5 }}>{c.tag}</span>
                {c.audio_files.length === 0 && (
                  <span style={{ fontSize:9, padding:"2px 8px", borderRadius:20, background:"#F5F5F5", color:"#7b7b7b", fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}>Pending audio</span>
                )}
              </div>
            </div>
            <div style={{ marginTop:8, fontSize:12, color: c.status_id === 3 ? "#B8B8B8" : "#F90091", opacity: c.status_id === 3 ? 0.5 : 1 }}>{c.lift ?? "—"}</div>
            {selected===c.id && (
              <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #E2E2E2" }} onClick={e => e.stopPropagation()}>
                <VariationsPlayer
                  variations={c.audio_files}
                  playingVariationId={playingVariationId}
                  isPlaying={isThisPlaying && isPlaying}
                  currentTime={isThisPlaying ? currentTime : 0}
                  duration={duration || 0}
                  volume={volume}
                  onPlayVariation={(v) => handlePlayVariation(c, v)}
                  onSeek={(t) => handleSeek(c, t)}
                  onVolumeChange={setVolume}
                  isSupportUser={isSupportUser}
                  togglingVariation={togglingVariation}
                  onToggleVariation={(e, v) => handleToggleVariation(e, c, v)}
                />
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginTop:10 }}>
                  <button onClick={() => { audioRef.current?.pause(); setReviewCreative(c); track('creative_reviewed', { creative_id: c.id, creative_name: c.name }); }} style={{ padding:"6px 12px", background:"transparent", color:"#626262", border:"1px solid #E2E2E2", borderRadius:6, fontSize:11, cursor:"pointer" }}>Review</button>
                  {isSupportUser && c.audio_files.length > 0 && (
                    <div
                      onClick={(e) => !toggling.has(c.id) && handleToggleActive(e, c)}
                      style={{ display:"flex", alignItems:"center", gap:6, cursor: toggling.has(c.id) ? "default" : "pointer", opacity: toggling.has(c.id) ? 0.5 : 1, flexShrink:0 }}
                    >
                      <div style={{ position:"relative", width:30, height:17, background: c.status_id !== 3 ? "#F90091" : "#E2E2E2", borderRadius:9, transition:"background 0.2s" }}>
                        <div style={{ position:"absolute", top:2, left: c.status_id !== 3 ? 15 : 2, width:13, height:13, background:"#fff", borderRadius:"50%", transition:"left 0.2s" }} />
                      </div>
                      <span style={{ fontSize:9, padding:"2px 7px", borderRadius:20, fontWeight:700, textTransform:"uppercase", letterSpacing:0.5, background: c.status_id === 1 ? "#FFE9F6" : "#F5F5F5", color: c.status_id !== 3 ? "#F90091" : "#B8B8B8" }}>
                        {c.tag}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
      {hiddenCount > 0 && (
        <div style={{ textAlign:"center", marginTop:16 }}>
          <button
            onClick={() => setShowAll(true)}
            style={{ background:"none", border:"1px solid #151515", borderRadius:8, color:"#151515", fontSize:13, fontWeight:600, cursor:"pointer", padding:"7px 20px" }}
          >
            {"Show all "}
            <span style={{ fontSize:11, fontWeight:400, opacity:0.7 }}>({sorted.length} creatives)</span>
          </button>
        </div>
      )}
      </>
      )}

      {showNewModal && (
        <NewCreativeModal
          advertiserId={advertiserId}
          canCreate={canManageAdvertiserData}
          onClose={() => setShowNewModal(false)}
          onCreated={() => setCreativesRefresh((r) => r + 1)}
        />
      )}
      {reviewCreative && (
        <ReviewCreativeModal creative={reviewCreative} onClose={() => setReviewCreative(null)} />
      )}
    </div>
  );
}
